import * as Dialog from "@radix-ui/react-dialog";
import {
  CircleAlert,
  Copy,
  Edit,
  FileText,
  X,
  CheckIcon,
  XIcon,
} from "lucide-react";
import type * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import type { Order } from "../../lib/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import EntityDropDown from "./EntityDropDown";
import { OrderHistory } from "./OrderHistory";
import OrderStatusDropdown from "./OrderStatusDropdown";
import {
  initialOrderStatus,
  opsStatus,
  orderStatusEnum,
} from "../../lib/constants";
import { Button } from "../ui/button";
import { decrypt } from "../../services/encryptionService";
import { toast, useToast } from "../../hooks/use-toast";
import { useAuth } from "../../lib/authContext";
import CancellationReasons from "./CancellationReasons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Input } from "../ui/input";

interface OrderDrawerProps {
  orderId: number | null;
  onClose: () => void;
}

export const OrderDrawer: React.FC<OrderDrawerProps> = ({
  orderId,
  onClose,
}) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [prescriptions, setPrescriptions] = useState([]);
  const [gettingPrescriptions, setGettingPrescriptions] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, accessLevel } = useAuth();
  const [logisticPartners, setLogisticPartners] = useState([]);

  useEffect(() => {
    if (orderId) {
      api
        .getOrder(orderId.toString())
        .then(response => {
          setOrder(response.data);
          setPrescriptions(response.data.prescriptions);
        })
        .catch(error => {
          toast({
            title: "Error fetching order details",
            description: error.response?.data?.message || error.message,
            variant: "destructive",
          });
          onClose();
        });
      api.getLogisticPartners().then(response => {
        setLogisticPartners(response.data);
      });
    }
  }, [orderId, toast]);

  const invoice_status =
    order?.order_history.find(
      (history: any) => history.status === orderStatusEnum.InvoiceGenerated,
    ) ||
    order?.order_history.find(
      (history: any) => history.status === orderStatusEnum.Dispatched,
    ) ||
    order?.order_history.find(
      (history: any) => history.status === orderStatusEnum.Delivered,
    );

  const reject_reason =
    order?.status === orderStatusEnum.Rejected
      ? order?.order_history.find(
          (history: any) => history.status === orderStatusEnum.Rejected,
        )?.api_body?.reject_reason
      : null;

  async function getPrescriptions() {
    try {
      setGettingPrescriptions(true);
      const prescriptions_data = await api.uploadPrescriptions(
        order?.invoice_number || "",
      );
      if (prescriptions_data.success) {
        setPrescriptions(prescriptions_data.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setGettingPrescriptions(false);
    }
  }

  const dispatch_status = order?.order_history.find(
    (history: any) => history.status === orderStatusEnum.Dispatched,
  )?.api_body;

  const handleBluedartTracking = () => {
    if (dispatch_status?.awb_number) {
      if (order?.id) {
        window.open(
          `/shipment_tracker?orderId=${order.invoice_number}`,
          "_blank",
        );
      }
    } else {
      toast({
        title: "AWB number not found",
        description: "Please check the order history for the AWB number",
        variant: "destructive",
      });
    }
  };

  const cancelOrder = async () => {
    if (!order?.id) {
      toast({
        title: "Something went wrong",
        description: "Please try again later",
        variant: "destructive",
      });
      return;
    }

    const reason = cancellationReason;

    if (!reason) {
      toast({
        title: "Cancellation reason required",
        description: "Please select a reason for cancellation",
        variant: "destructive",
      });
      return;
    }

    if (window.confirm("Are you sure you want to cancel this order?")) {
      await api.cancelOrder(
        order?.invoice_number as string,
        user?.id as string,
        reason,
      );
      toast({
        title: "Order cancelled successfully",
        description: "The order has been cancelled",
        variant: "default",
      });
      onClose();
    }
  };

  return (
    <Dialog.Root open={!!orderId} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-4/5 w-4/5 bg-white dark:bg-gray-900 shadow-lg overflow-auto">
          <Dialog.Title className="text-2xl font-bold text-gray-900 dark:text-white pl-4 pt-4">
            <div className="flex items-center">
              <div>Order Details #{order?.invoice_number}</div>
              <Copy
                className="cursor-pointer text-sm text-gray-500 ml-3 dark:text-gray-400"
                onClick={() => {
                  navigator.clipboard.writeText(order?.invoice_number || "");
                  toast({
                    title: "Invoice number copied to clipboard",
                  });
                }}
              />
            </div>
          </Dialog.Title>
          <div className="p-4 flex mt-3">
            <div className="w-2/5 mr-4">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="absolute top-4 right-4 inline-flex items-center justify-center rounded-full h-10 w-10 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-300"
                  aria-label="Close"
                >
                  <X />
                </button>
              </Dialog.Close>
              {order?.id && (
                <>
                  <OrderMetadata
                    order={order}
                    logisticPartners={logisticPartners}
                  />
                  <OrderActions
                    order={order}
                    onClose={() => {
                      onClose();
                      setOrder(null);
                    }}
                  />
                </>
              )}
            </div>
            <div className="border-l border-gray-200 dark:border-gray-700 w-1 mx-4" />
            <div className="w-3/5">
              <div className="mb-2 flex justify-between">
                {invoice_status && (
                  <div className="flex items-center">
                    <div>Invoice : </div>
                    <FileText
                      className="text-right cursor-pointer"
                      onClick={() =>
                        window.open(
                          invoice_status.api_body.InvoiceUrl[0].url,
                          "_blank",
                        )
                      }
                      onKeyUp={e => {
                        if (e.key === "Enter") {
                          window.open(
                            invoice_status.InvoiceUrl[0].url,
                            "_blank",
                          );
                        }
                      }}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          window.open(
                            invoice_status.InvoiceUrl[0].url,
                            "_blank",
                          );
                        }
                      }}
                    />
                  </div>
                )}
                {reject_reason && (
                  <div className="flex items-center">
                    <div className="text-red-500 dark:text-red-400">
                      Reject Reason : {reject_reason}
                    </div>
                  </div>
                )}
                {dispatch_status && (
                  <div className="flex items-center">
                    <div
                      className="text-blue-500 dark:text-blue-400 mt-2 link hover:underline cursor-pointer"
                      onClick={handleBluedartTracking}
                      onKeyUp={e => {
                        if (e.key === "Enter") {
                          handleBluedartTracking();
                        }
                      }}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleBluedartTracking();
                        }
                      }}
                    >
                      Shipment Tracking Link
                    </div>
                  </div>
                )}
                <div className="flex items-center">
                  Prescriptions :
                  {prescriptions ? (
                    prescriptions.map((prescription: string, index: number) => (
                      <FileText
                        key={prescription}
                        className="text-right cursor-pointer"
                        onClick={() => window.open(prescription, "_blank")}
                        onKeyUp={e => {
                          if (e.key === "Enter") {
                            window.open(prescription, "_blank");
                          }
                        }}
                        onKeyDown={e => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            window.open(prescription, "_blank");
                          }
                        }}
                      />
                    ))
                  ) : gettingPrescriptions ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500">
                      .
                    </div>
                  ) : (
                    <Button onClick={() => getPrescriptions()}>
                      Get Prescriptions
                    </Button>
                  )}
                </div>
              </div>
              {order && (
                <Tabs defaultValue="actions" className="mt-6">
                  <TabsList className="grid w-full grid-cols-2 dark:bg-gray-800">
                    <TabsTrigger
                      value="actions"
                      className="dark:data-[state=active]:bg-gray-700 dark:text-gray-300"
                    >
                      Items
                    </TabsTrigger>
                    <TabsTrigger
                      value="history"
                      className="dark:data-[state=active]:bg-gray-700 dark:text-gray-300"
                    >
                      History
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="actions" className="dark:text-gray-300">
                    <OrderItems order={order} />
                  </TabsContent>
                  <TabsContent value="history" className="dark:text-gray-300">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                      <OrderHistory order={order} onClose={onClose} />
                    </div>
                  </TabsContent>
                </Tabs>
              )}
              {(!accessLevel?.access_level?.provider_id ||
                accessLevel.access_level.provider_id.length === 0) &&
                order?.status !== orderStatusEnum.Cancelled &&
                order?.status !== orderStatusEnum.Rejected &&
                order?.status !== orderStatusEnum.Pending && (
                  <div className="flex items-center justify-end gap-2">
                    <CancellationReasons
                      value={cancellationReason}
                      onChange={setCancellationReason}
                    />
                    <Button
                      variant="destructive"
                      className="bg-red-500 text-white"
                      onClick={cancelOrder}
                      disabled={!cancellationReason}
                    >
                      Cancel Order
                    </Button>
                  </div>
                )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

const OrderItems: React.FC<{ order: Order }> = ({ order }) => {
  const onHold = order.status === orderStatusEnum.OnHold;
  const status_history = order?.order_history.find(
    (history: any) => history.status === orderStatusEnum.OnHold,
  )?.api_body;
  const billed_items = status_history?.ItemDetails;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase"
            >
              Item
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase"
            >
              Product Name
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-xs font-medium tracking-wider text-gray-500 uppercase"
            >
              Ordered Quantity
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-xs font-medium tracking-wider text-gray-500 uppercase"
            >
              Billed Quantity
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-xs font-medium tracking-wider text-gray-500 uppercase"
            >
              Price
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-xs font-medium tracking-wider text-gray-500 uppercase"
            >
              Discount
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-xs font-medium tracking-wider text-gray-500 uppercase"
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {order.items.map(item => (
            <tr key={item.product_id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {item.product_id}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {item.product_metadata?.name}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-right text-gray-500">
                  {item.sale_quantity}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-right text-gray-500">
                  {item.billed_quantity || "N/A"}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-right text-gray-500">
                  {item.selling_price.toFixed(1)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-right text-gray-500">
                  {item.product_metadata?.discount || "N/A"}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                {(
                  (item.selling_price -
                    (item.selling_price *
                      (item.product_metadata?.discount || 0)) /
                      100) *
                  item.sale_quantity
                ).toFixed(1)}
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={4} className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-right font-medium text-gray-900">
                Total
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
              {order.items
                .reduce(
                  (acc, item) => acc + item.selling_price * item.sale_quantity,
                  0,
                )
                .toFixed(1)}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm font-medium text-gray-900">Net Total</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
              {order.items
                .reduce(
                  (acc, item) =>
                    acc +
                    (item.selling_price -
                      (item.selling_price *
                        (item.product_metadata?.discount || 0)) /
                        100) *
                      item.sale_quantity,
                  0,
                )
                .toFixed(1)}
            </td>
          </tr>
        </tbody>
      </table>
      {onHold && billed_items && (
        <>
          <div className="text-md flex items-center justify-between gap-2 font-medium my-2 text-gray-900 dark:text-gray-300">
            <span>[SP360] Billed Items</span>
            <span className="text-gray-900 dark:text-gray-300 text-sm flex items-center gap-2">
              <CircleAlert className="w-4 h-4 text-red-500 dark:text-red-400" />
              {status_history?.RequestType}
            </span>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase"
                >
                  Item
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase"
                >
                  Product Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase"
                >
                  Ordered Quantity
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase"
                >
                  Billed Quantity
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {billed_items.map((item: any) => (
                <tr key={item.product_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {item.ItemId}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {item.ItemName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-right text-gray-500">
                      {item.ReqQty}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-right text-gray-500">
                      {item.IssuedQty}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

const OrderMetadata: React.FC<{ order: Order; logisticPartners: any }> = ({
  order,
  logisticPartners,
}) => {
  const { user, accessLevel } = useAuth();
  const navigate = useNavigate();
  const [editAwb, setEditAwb] = useState(false);
  const [awbNumber, setAwbNumber] = useState(order.api_body.awb_number);
  const [selectedLogisticsPartner, setSelectedLogisticsPartner] = useState(
    logisticPartners[0].id,
  );
  const dispatch_status = order.order_history.find(
    (history: any) => history.status === orderStatusEnum.Dispatched,
  )?.api_body;

  const submitAwb = async () => {
    const update_awb = await api.updateAwbDetails({
      invoice_number: order.invoice_number ?? "",
      awb_number: awbNumber,
      transporter: selectedLogisticsPartner,
      user_id: user?.id ?? "",
    });
    if (update_awb) {
      toast({
        title: "AWB updated",
        description: `AWB ${awbNumber} updated successfully for ${order.invoice_number}`,
      });
    }
    setEditAwb(false);
    navigate("/orders");
  };
  return (
    <div className="space-y-2">
      {/* Converted to borderless table for better alignment */}
      <table className="w-full text-sm">
        <tbody>
          <tr>
            <th className="font-medium text-gray-400 text-left w-1/3">
              Vendor
            </th>
            <td className="text-right">{order.provider_name}</td>
          </tr>
          {(order.api_body?.Otp || order.api_body?.otp) &&
            (!accessLevel?.access_level?.provider_id ||
              accessLevel.access_level.provider_id.length === 0) && (
              <tr>
                <th className="font-medium text-gray-400 text-left w-1/3">
                  OTP
                </th>
                <td className="text-right">
                  {order.api_body?.Otp || order.api_body?.otp}
                </td>
              </tr>
            )}
          <tr>
            <th className="font-medium text-gray-400 text-left">
              Customer Name
            </th>
            <td className="flex items-center gap-2 justify-end">
              {order.patient?.name} - {order.patient?.phone}
              <Copy
                className="cursor-pointer text-sm text-gray-500 ml-1 w-4 h-4 dark:text-gray-400"
                onClick={() => {
                  navigator.clipboard.writeText(order.patient?.phone || "");
                  toast({
                    title: "Phone number copied to clipboard",
                  });
                }}
              />
            </td>
          </tr>
          <tr>
            <th className="font-medium text-gray-400 text-left">
              Order placed at
            </th>
            <td className="text-right">
              {new Date(order.created_at).toLocaleString()}
            </td>
          </tr>
          <tr>
            <th className="font-medium text-gray-400 text-left">TAT</th>
            <td className="text-right">{order.tat}</td>
          </tr>
          <tr>
            <th className="font-medium text-gray-400 text-left">AWB Number</th>
            <td className="text-right">
              <div className="flex items-center gap-2 justify-end">
                {dispatch_status?.awb_number || "N/A"}
                {[
                  orderStatusEnum.Dispatched,
                  orderStatusEnum.InvoiceGenerated,
                  orderStatusEnum.Billed,
                  orderStatusEnum.RTO,
                  orderStatusEnum.Transit,
                  orderStatusEnum.OnHold,
                ].includes(order.status as orderStatusEnum) && (
                  <Edit
                    className="cursor-pointer text-sm text-gray-500 ml-1 w-4 h-4 dark:text-gray-400"
                    onClick={() => setEditAwb(true)}
                  />
                )}
              </div>
            </td>
          </tr>
          {editAwb && (
            <tr>
              <td colSpan={2}>
                <div className="flex items-center gap-2 ">
                  <Select
                    value={
                      logisticPartners.filter(
                        (partner: { id: number; name: string }) =>
                          partner.id === selectedLogisticsPartner,
                      )[0]?.name
                    }
                    onValueChange={value => {
                      setSelectedLogisticsPartner(
                        logisticPartners.filter(
                          (partner: { id: number; name: string }) =>
                            partner.name === value,
                        )[0]?.id,
                      );
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select AWB Number" />
                    </SelectTrigger>
                    <SelectContent>
                      {logisticPartners.map((partner: any) => (
                        <SelectItem key={partner.id} value={partner.name}>
                          {partner.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input
                    type="text"
                    value={awbNumber}
                    onChange={e => setAwbNumber(e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-2 my-2"
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      submitAwb();
                    }}
                  >
                    <CheckIcon className="w-4 h-4 text-green-500 dark:text-green-400" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditAwb(false);
                    }}
                  >
                    <XIcon className="w-4 h-4 text-red-500 dark:text-red-400" />
                  </Button>
                </div>
              </td>
            </tr>
          )}
          <tr>
            <th className="font-medium text-gray-400 text-left">
              Delivery Mode
            </th>
            <td className="text-right">{order.mode}</td>
          </tr>
          <tr>
            <th className="font-medium text-gray-400 text-left">
              Payment Type
            </th>
            <td className="text-right">{order.is_cod ? "COD" : "Prepaid"}</td>
          </tr>
          {(order.api_body?.deliverymode === "STORE_PICKUP" ||
            order.api_body?.delivery_mode === "STORE_PICKUP") && (
            <tr>
              <th className="font-medium text-gray-400 text-left">
                Pickup Store
              </th>
              <td className="text-right">{order.entity_name}</td>
            </tr>
          )}
        </tbody>
      </table>
      <span className="w-full">{order.patient?.address?.shipping_address}</span>
      {order.api_body.latitude && order.api_body.longitude && (
        <span
          className="cursor-pointer text-blue-500 dark:text-blue-400 mt-2 ml-2 link hover:underline"
          onClick={() => {
            window.open(
              `https://www.google.com/maps/search/?api=1&query=${order.api_body.latitude},${order.api_body.longitude}`,
              "_blank",
            );
          }}
          onKeyUp={e => {
            if (e.key === "Enter") {
              window.open(
                `https://www.google.com/maps/search/?api=1&query=${order.api_body.latitude},${order.api_body.longitude}`,
                "_blank",
              );
            }
          }}
        >
          Map Location
        </span>
      )}
    </div>
  );
};

const OrderActions: React.FC<{ order: Order; onClose: () => void }> = ({
  order,
  onClose,
}) => {
  const opsAdmins = process.env.REACT_APP_OPS_ADMINS;
  const { user, accessLevel } = useAuth();
  const { toast } = useToast();

  const handleMarkAsCod = async (isPrepaid: boolean) => {
    if (window.confirm(`Are you sure you want to mark this order as COD?`)) {
      const response = await api.markAsCod(order.invoice_number || "", false);
      if (response.success) {
        toast({
          title: "Success",
          description: response.message,
        });
        onClose();
      } else {
        toast({
          title: "Error",
          description: response.error || "Something went wrong",
          variant: "destructive",
        });
      }
    }
  };

  const isCOD = order.is_cod;

  return (
    <div>
      <div className="space-y-2">
        {(!accessLevel?.access_level?.provider_id ||
          accessLevel.access_level.provider_id.length === 0) &&
          opsStatus.includes(order.status) && (
            <OrderStatusDropdown
              currentStatus={order.status}
              orderId={Number(order.id)}
            />
          )}
        {opsAdmins &&
          opsAdmins.includes(user?.email ?? "") &&
          (!accessLevel?.access_level?.provider_id ||
            accessLevel.access_level.provider_id.length === 0) &&
          initialOrderStatus.includes(order.status) && (
            <EntityDropDown
              orderId={order.id}
              invoiceNumber={order.invoice_number ?? ""}
              providerName={order.provider_name ?? ""}
              onClose={() => onClose}
            />
          )}
        {(!accessLevel?.access_level?.provider_id ||
          accessLevel.access_level.provider_id.length === 0) &&
          !isCOD && (
            <Button
              className="w-full"
              variant="outline"
              onClick={() => handleMarkAsCod(true)}
            >
              Mark as COD
            </Button>
          )}
      </div>
    </div>
  );
};
